// @ts-nocheck
// Ported byte-identically from v1
// components/partials/components/credits-wallet-modal.js.
// Popover anchored under the Credits button in the header. Renders three
// rows (Voice / Effective Voice / TTS), a refresh icon, and a "View
// History" link on the Effective Voice row. Dismisses on outside-click
// or Escape.
import { useEffect, useRef } from 'react';
import { Wallet, Phone, Volume2, Bot, RefreshCw, X } from 'lucide-react';
import { toRupeeFixed } from '@/utilities/helpers';

const formatAmount = (amount) => {
  const n = Number(amount) || 0;
  return n % 1 === 0 ? String(n) : toRupeeFixed(n);
};

interface Props {
  show: boolean;
  onHide: () => void;
  containerRef?: any;
  voiceCredits: any;
  effectiveVoiceCredits: any;
  ttsCredits: any;
  effectiveTtsCredits: any;
  aiAgentCredits?: any;
  effectiveAiAgentCredits?: any;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  onViewHistory?: () => void;
  onViewTtsHistory?: () => void;
  onViewAiAgentHistory?: () => void;
  hideEffectiveVoiceCredits?: boolean;
}

export default function CreditsWalletPopover({
  show,
  onHide,
  containerRef,
  voiceCredits,
  effectiveVoiceCredits,
  ttsCredits,
  effectiveTtsCredits,
  aiAgentCredits,
  effectiveAiAgentCredits,
  onRefresh,
  isRefreshing,
  onViewHistory,
  onViewTtsHistory,
  onViewAiAgentHistory,
  hideEffectiveVoiceCredits,
}: Props) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!show) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        panelRef.current?.contains(e.target as Node) ||
        containerRef?.current?.contains(e.target as Node)
      ) {
        return;
      }
      onHide();
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onHide();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [show, onHide, containerRef]);

  if (!show) return null;

  const rows = [
    { key: 'voice', icon: Phone, iconBg: '#dbeafe', iconColor: '#2563eb', label: 'Voice Credits', value: formatAmount(voiceCredits) },
    ...(hideEffectiveVoiceCredits
      ? []
      : [{ key: 'effective', icon: Wallet, iconBg: '#dcfce7', iconColor: '#16a34a', label: 'Effective Voice Credits', value: formatAmount(effectiveVoiceCredits), showHistory: true, historyKind: 'voice' }]),
    { key: 'tts', icon: Volume2, iconBg: '#ede9fe', iconColor: '#7c3aed', label: 'TTS Credits', value: formatAmount(ttsCredits) },
    ...(hideEffectiveVoiceCredits
      ? []
      : [{ key: 'effectiveTts', icon: Wallet, iconBg: '#f3e8ff', iconColor: '#7c3aed', label: 'Effective TTS Credits', value: formatAmount(effectiveTtsCredits), showHistory: true, historyKind: 'tts' }]),
    { key: 'aiAgent', icon: Bot, iconBg: '#ccfbf1', iconColor: '#0f766e', label: 'AI Agent Voice Credits', value: formatAmount(aiAgentCredits) },
    ...(hideEffectiveVoiceCredits
      ? []
      : [{ key: 'effectiveAiAgent', icon: Wallet, iconBg: '#f0fdfa', iconColor: '#0f766e', label: 'Effective AI Agent Voice Credits', value: formatAmount(effectiveAiAgentCredits), showHistory: true, historyKind: 'aiAgent' }]),
  ];

  return (
    <>
      <style>{`
        .credits-wallet-popover {
          position: absolute;
          top: calc(100% + 10px);
          right: 0;
          width: min(360px, calc(100vw - 24px));
          z-index: 1100;
          background: #ffffff;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18);
          overflow: hidden;
          animation: creditsWalletFadeIn 0.15s ease-out;
        }
        .credits-wallet-popover::before {
          content: "";
          position: absolute;
          top: -6px;
          right: 24px;
          width: 12px;
          height: 12px;
          background: #1e2a55;
          border-left: 1px solid #e2e8f0;
          border-top: 1px solid #e2e8f0;
          transform: rotate(45deg);
        }
        /* Below md the wallet button sits near the left edge; a right-anchored
           popover would run off-screen, so pin it to the viewport instead. */
        @media (max-width: 767.98px) {
          .credits-wallet-popover {
            position: fixed;
            top: 60px;
            right: 12px;
            max-height: calc(100dvh - 72px);
            overflow-y: auto;
          }
          .credits-wallet-popover::before {
            display: none;
          }
        }
        @keyframes creditsWalletFadeIn {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .credits-wallet-popover__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 12px;
          background: linear-gradient(135deg, #1e2a55, #2f4b8f);
          color: white;
        }
        .credits-wallet-popover__title {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          font-weight: 600;
        }
        .credits-wallet-popover__actions {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .credits-wallet-popover__icon-btn {
          background: rgba(255,255,255,0.15);
          border: none;
          border-radius: 6px;
          padding: 4px;
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .credits-wallet-popover__icon-btn:hover {
          background: rgba(255,255,255,0.25);
        }
        .credits-wallet-popover__icon-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .credits-wallet-popover__body {
          padding: 8px;
        }
        .credits-wallet-popover__row {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 10px;
          border-radius: 8px;
        }
        .credits-wallet-popover__row:hover {
          background: #f8fafc;
        }
        .credits-wallet-popover__row-icon {
          width: 30px;
          height: 30px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .credits-wallet-popover__row-label {
          flex: 1;
          font-size: 12px;
          color: #64748b;
          font-weight: 500;
        }
        .credits-wallet-popover__row-value {
          font-size: 14px;
          font-weight: 700;
          color: #0f172a;
        }
        body.dark .credits-wallet-popover {
          background: #0f172a;
          border-color: #334155;
        }
        body.dark .credits-wallet-popover__row:hover {
          background: #1e293b;
        }
        .credits-wallet-popover__row-value-wrap {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
          margin-left: auto;
        }
        .credits-wallet-popover__history-btn {
          background: #ecfdf5;
          border: 1px solid #86efac;
          color: #15803d;
          font-size: 10px;
          font-weight: 600;
          padding: 3px 8px;
          border-radius: 6px;
          cursor: pointer;
          white-space: nowrap;
          line-height: 1.2;
        }
        .credits-wallet-popover__history-btn:hover {
          background: #dcfce7;
        }
        body.dark .credits-wallet-popover__row-value {
          color: #f1f5f9;
        }
      `}</style>
      <div ref={panelRef} className="credits-wallet-popover">
        <div className="credits-wallet-popover__header">
          <div className="credits-wallet-popover__title">
            <Wallet size={15} />
            Credits Wallet
          </div>
          <div className="credits-wallet-popover__actions">
            <button
              type="button"
              className="credits-wallet-popover__icon-btn"
              onClick={onRefresh}
              disabled={isRefreshing}
              title="Refresh"
            >
              <RefreshCw
                size={14}
                style={{ animation: isRefreshing ? 'spin 1s linear infinite' : 'none' }}
              />
            </button>
            <button
              type="button"
              className="credits-wallet-popover__icon-btn"
              onClick={onHide}
              title="Close"
            >
              <X size={14} />
            </button>
          </div>
        </div>
        <div className="credits-wallet-popover__body">
          {rows.map((row) => {
            const Icon = row.icon;
            return (
              <div key={row.key} className="credits-wallet-popover__row">
                <div
                  className="credits-wallet-popover__row-icon"
                  style={{ backgroundColor: row.iconBg, color: row.iconColor }}
                >
                  <Icon size={15} />
                </div>
                <span className="credits-wallet-popover__row-label">{row.label}</span>
                {row.showHistory ? (
                  <div className="credits-wallet-popover__row-value-wrap">
                    {((row.historyKind === 'tts' && onViewTtsHistory) ||
                      (row.historyKind === 'aiAgent' && onViewAiAgentHistory) ||
                      (row.historyKind === 'voice' && onViewHistory)) && (
                      <button
                        type="button"
                        className="credits-wallet-popover__history-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (row.historyKind === 'tts') onViewTtsHistory?.();
                          else if (row.historyKind === 'aiAgent') onViewAiAgentHistory?.();
                          else onViewHistory?.();
                        }}
                      >
                        View History
                      </button>
                    )}
                    <span className="credits-wallet-popover__row-value">{row.value}</span>
                  </div>
                ) : (
                  <span className="credits-wallet-popover__row-value">{row.value}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
